/* eslint-disable */
import { MeshStandardMaterial, MeshPhysicalMaterial, Vector3 } from 'three';

import * as Helpers from '../helpers';

import { Object3D } from '../Object3D';

export default class IndustrialDoor extends Object3D {
  constructor(...args) {
    super(...args);

    this.materials = {
      'double-mirror': new MeshPhysicalMaterial(),
      metal: new MeshStandardMaterial(),
      milky: new MeshPhysicalMaterial(),
      security: new MeshPhysicalMaterial(),
      transparent: new MeshPhysicalMaterial(),
      wood: new MeshPhysicalMaterial(),
    };

    this.parameters.frameWidth = 20;
    this.parameters.frameThickness = 40;
    this.parameters.frameMargin = 1;

    this.parameters.materialThickness = 2;
    this.parameters.parcloseDepth = 15;
    this.parameters.glassThickness = 6;
    this.parameters.hangerMargin = 90;

    this.parameters.slatSize = 100;
    this.parameters.slatThickness = 18;
  }

  calculateModelParameters() {
    this.parameters.parcloseLength =
      (this.parameters.height -
        (2 + this.parameters.density) * this.parameters.frameWidth) /
      (this.parameters.density + 1);
    this.parameters.parcloseWidth =
      this.parameters.width -
      2 * (this.parameters.frameWidth + this.parameters.parcloseDepth);

    this.parameters.slatLength =
      (this.parameters.height -
        (2 + this.parameters.density) * this.parameters.frameWidth) /
      (this.parameters.density + 1);
    this.parameters.slatWidth =
      this.parameters.width - 2 * this.parameters.frameWidth;
  }

  buildSlatGeometry() {
    const geometries = [];
    /* create the base geometries */
    const slats = Helpers.CreateSlats(
      this.parameters.slatWidth,
      this.parameters.slatLength,
      this.parameters.slatSize,
      this.parameters.slatThickness,
      this.parameters.style
    );

    const slatsAngle = this.getOrientation();

    /* create mirror and duplicate the elements */
    for (let i = 0; i < this.parameters.density + 1; i++) {
      slats.forEach((s) => {
        let slat = s.clone();
        if (i % 2 === 0) {
          slat = Helpers.MirrorObjectZ(slat, false);
          slat.translate(0, 0, this.parameters.slatLength);
        }
        slat.translate(
          this.parameters.frameWidth,
          0,
          this.parameters.frameWidth +
            i * (this.parameters.slatLength + this.parameters.frameWidth)
        );
        geometries.push(
          this.updateGeometry(slat, Math.pow(-1, i) * slatsAngle)
        );
      });
    }
    slats.forEach((s) => {
      s.dispose();
    }); // Clean geometries to avoid leak

    return geometries;
  }

  buildFrameGeometry() {
    const geometries = [];

    /* create the base geometries */
    const leftStile = Helpers.CreateOFrame(
      this.parameters.height,
      this.parameters.frameWidth,
      this.parameters.frameThickness,
      this.parameters.materialThickness,
      true,
      false,
      this.parameters.hangerMargin
    );
    const rightStile = Helpers.MirrorObjectX(leftStile, true);
    const topRail = Helpers.CreateOFrame(
      this.parameters.width,
      this.parameters.frameWidth,
      this.parameters.frameThickness,
      this.parameters.materialThickness,
      true,
      this.parameters.system === 'hamak' ? true : false,
      this.parameters.hangerMargin
    );
    const bottomRail = Helpers.CreateOFrame(
      this.parameters.width,
      this.parameters.frameWidth,
      this.parameters.frameThickness,
      this.parameters.materialThickness,
      true,
      false,
      this.parameters.hangerMargin
    );
    /* position the geometries */
    leftStile.translate(0, -this.parameters.frameThickness / 2, 0);
    rightStile.translate(
      this.parameters.width,
      -this.parameters.frameThickness / 2,
      0
    );
    Helpers.RotateObject(topRail, null, 90, 'Y');
    topRail.translate(
      0,
      -this.parameters.frameThickness / 2,
      this.parameters.height
    );
    Helpers.RotateObject(bottomRail, null, -90, 'Y');
    bottomRail.translate(
      this.parameters.width,
      -this.parameters.frameThickness / 2,
      0
    );

    /* push the geometries into the array */
    geometries.push(leftStile);
    geometries.push(rightStile);
    geometries.push(topRail);
    geometries.push(bottomRail);

    /* create the middle rail */
    for (let i = 0; i < this.parameters.density; i++) {
      const middleRail = Helpers.CreateOFrame(
        this.parameters.width - 2 * this.parameters.frameWidth,
        this.parameters.frameWidth,
        this.parameters.frameThickness,
        this.parameters.materialThickness,
        false,
        false,
        this.parameters.hangerMargin
      );
      Helpers.RotateObject(middleRail, null, 90, 'Y');
      middleRail.translate(
        this.parameters.frameWidth,
        -this.parameters.frameThickness / 2,
        this.parameters.frameWidth +
          (i + 1) *
            (this.parameters.frameWidth + this.parameters.parcloseLength)
      );
      geometries.push(this.updateGeometry(middleRail));
    }

    return geometries;
  }

  buildParcloseGeometry() {
    const geometries = [];
    let parcloses = [];
    /* Create the base objects */
    const pcStile = Helpers.CreateOFrame(
      this.parameters.parcloseLength - 2 * this.parameters.frameMargin,
      this.parameters.parcloseDepth,
      this.parameters.parcloseDepth,
      this.parameters.materialThickness,
      false,
      false,
      this.parameters.hangerMargin
    );
    const pcRail = Helpers.CreateOFrame(
      this.parameters.parcloseWidth - 2 * this.parameters.frameMargin,
      this.parameters.parcloseDepth,
      this.parameters.parcloseDepth,
      this.parameters.materialThickness,
      false,
      false,
      this.parameters.hangerMargin
    );

    /* Position the first parclose square */
    pcStile.translate(
      this.parameters.frameWidth + this.parameters.frameMargin,
      -this.parameters.frameThickness / 2,
      this.parameters.frameWidth + this.parameters.frameMargin
    );
    parcloses.push(pcStile);

    let pcClone = pcStile.clone();
    pcClone.translate(
      this.parameters.width -
        2 * this.parameters.frameWidth -
        this.parameters.parcloseDepth -
        2 * this.parameters.frameMargin,
      0,
      0
    );
    parcloses.push(pcClone);

    Helpers.RotateObject(pcRail, null, 90, 'Y');
    pcRail.translate(
      this.parameters.frameWidth +
        this.parameters.parcloseDepth +
        this.parameters.frameMargin,
      -this.parameters.frameThickness / 2,
      this.parameters.frameWidth +
        this.parameters.parcloseDepth +
        this.parameters.frameMargin
    );
    parcloses.push(pcRail);

    pcClone = pcRail.clone();
    pcClone.translate(
      0,
      0,
      this.parameters.parcloseLength -
        this.parameters.parcloseDepth -
        2 * this.parameters.frameMargin
    );
    parcloses.push(pcClone);

    /* Mirror the parclose to the other side */
    const parclosesMirrored = [];
    parcloses.forEach((z) => {
      pcClone = z.clone();
      pcClone.translate(
        0,
        this.parameters.frameThickness - this.parameters.parcloseDepth,
        0
      );
      parclosesMirrored.push(pcClone);
    });
    parcloses = parcloses.concat(parclosesMirrored);

    /* Copy the parclose for each the cell */
    for (let i = 0; i <= this.parameters.density; i++) {
      parcloses.forEach((z) => {
        if (i === 0) {
          geometries.push(z);
        } else {
          pcClone = z.clone();
          pcClone.translate(
            0,
            0,
            (this.parameters.parcloseLength + this.parameters.frameWidth) * i
          );
          geometries.push(pcClone);
        }
      });
    }

    return geometries;
  }

  buildDecoGeometry() {
    const geometries = [];
    const decoration = [];
    /* Create the base objects */
    const element = Helpers.CreateOFrame(
      this.parameters.parcloseLength -
        2 * this.parameters.parcloseDepth -
        4 * this.parameters.frameMargin,
      this.parameters.parcloseDepth,
      this.parameters.parcloseDepth,
      this.parameters.materialThickness,
      false,
      false,
      this.parameters.hangerMargin
    );
    element.translate(
      this.parameters.width / 2 - this.parameters.parcloseDepth / 2,
      -this.parameters.frameThickness / 2,
      this.parameters.frameWidth +
        this.parameters.parcloseDepth +
        2 * this.parameters.frameMargin
    );
    decoration.push(element);

    const tmp = element.clone();
    tmp.translate(
      0,
      this.parameters.frameThickness - this.parameters.parcloseDepth,
      0
    );
    decoration.push(tmp);

    /* Copy the element for each the cell */
    for (let i = 0; i <= this.parameters.density; i++) {
      decoration.forEach((z) => {
        if (i === 0) {
          geometries.push(z);
        } else {
          const tmp = z.clone();
          tmp.translate(
            0,
            0,
            (this.parameters.parcloseLength + this.parameters.frameWidth) * i
          );
          geometries.push(tmp);
        }
      });
    }

    return geometries;
  }

  buildBodyGeometry() {
    const geometries = [];
    /* Create the base objects */
    const plate = Helpers.Box2Points(
      new Vector3(
        this.parameters.frameWidth,
        0 - this.parameters.glassThickness / 2,
        this.parameters.frameWidth
      ),
      new Vector3(
        this.parameters.frameWidth +
          this.parameters.parcloseWidth +
          2 * this.parameters.parcloseDepth,
        0 + this.parameters.glassThickness / 2,
        this.parameters.frameWidth + this.parameters.parcloseLength
      )
    );

    const bodyAngle = this.getOrientation();
    /* Copy the element for each the cell */
    for (let i = 0; i <= this.parameters.density; i++) {
      if (i === 0) {
        geometries.push(
          this.updateGeometry(plate, Math.pow(-1, i + 1) * bodyAngle)
        );
      } else {
        const tmp = plate.clone();
        tmp.translate(
          0,
          0,
          i * (this.parameters.slatLength + this.parameters.frameWidth)
        );

        geometries.push(
          this.updateGeometry(tmp, Math.pow(-1, i + 1) * bodyAngle)
        );
      }
    }

    return geometries;
  }

  buildHangerGeometry() {
    const geometries = [];
    const hangerOffset = -20;
    const hangerObj = this.resourceManager.getObject('wheelSystem');
    if (!hangerObj) {
      return [];
    }

    hangerObj.children.forEach((z) => {
      const geom = z.geometry.clone();
      Helpers.RotateObject(geom, null, 90, 'X');

      geom.translate(
        this.parameters.hangerMargin,
        hangerOffset,
        this.parameters.height
      );
      const geom2 = geom.clone();
      geom2.translate(
        this.parameters.width - 2 * this.parameters.hangerMargin,
        0,
        0
      );

      if (this.parameters.system === 'hamak') {
        geometries.push(geom);
        geometries.push(geom2);
      }
    });

    // hangerObj.traverse((node) => {
    //   this.disposeObject(node);
    // }); // Clean geometries to avoid memory leak

    return geometries;
  }

  getOrientation() {
    if (this.parameters.style === 'horizontal') {
      return 0;
    }
    if (this.parameters.style === 'oblic') {
      return 45;
    }
    return 90;
  }
}
